"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { RootState, AppDispatch } from "../../store";
import {
  fetchSubscriptionConfig,
  saveSubscriptionConfig,
  patchSubscriptionBilling,
  patchSubscriptionApproval,
} from "../../store/modules/SubscriptionsAdmin/thunks";
import { usePermission } from "../usePermission/usePermission";

export type BillingStatus = "trial" | "active" | "past_due" | "inactive";
export type ChargeSource = "system" | "establishment_balance" | "pix";

export interface AdminSubscriptionConfigInput {
  percentage_to_pay: number;
  simultaneous_schedule_limit: number;
  weekly_schedule_limit?: number;
  sale_commission_percentage: number;
  company_type: "MEI" | "ME" | "EPP" | "LTDA" | "SA" | "PF";
  monthly_revenue: number;
  birth_date?: string | null;
  pix_key_type?: "cpf" | "cnpj" | "email" | "phone" | "random" | null;
  pix_key?: string | null;
  approval_status?: "pending_approval" | "approved" | "rejected" | null;
  pix_key_status?: "pending_review" | "approved" | "rejected" | null;
  free_trial_months?: number | null;
  preferred_charge_source?: ChargeSource | null;
}

export interface BillingPatchInput {
  free_trial_months?: number;
  trial_started_at?: string; // yyyy-MM-dd HH:mm:ss
  billing_status?: BillingStatus;
  last_paid_at?: string; // yyyy-MM-dd HH:mm:ss
  next_due_date?: string; // yyyy-MM-dd
  preferred_charge_source?: ChargeSource;
}

const useSubscriptionConfig = (establishmentId?: number) => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasPermission } = usePermission();

  const { data, loading, error } = useSelector(
    (s: RootState) => s.subscriptionConfig,
  );
  const [fetchedId, setFetchedId] = useState<number | null>(null);

  // Fetch once per establishment
  useEffect(() => {
    if (!establishmentId) return;
    if (fetchedId === establishmentId) return;
    if (!hasPermission("read establishment")) return;

    dispatch(fetchSubscriptionConfig({ establishment: establishmentId }));
    setFetchedId(establishmentId);
  }, [dispatch, establishmentId, fetchedId, hasPermission]);

  const upsertFull = useCallback(
    async (
      payload: AdminSubscriptionConfigInput,
      method: "post" | "put" = "post",
    ) => {
      if (!establishmentId) return;
      if (!hasPermission("update establishment")) return;
      const res = await dispatch(
        saveSubscriptionConfig({
          establishment: establishmentId,
          data: payload,
          method,
        }),
      );
      return res;
    },
    [dispatch, establishmentId, hasPermission],
  );

  const patchBilling = useCallback(
    async (payload: BillingPatchInput) => {
      if (!establishmentId) return;

      try {
        // Buscar configuração atual para decidir entre POST (criar) e PATCH (atualizar billing)
        const fetched: any = await dispatch(
          fetchSubscriptionConfig({
            establishment: establishmentId,
          }) as any,
        ).unwrap();

        const hasConfigId = Boolean(fetched?.id || fetched?.data?.id);
        if (!hasConfigId) {
          await dispatch(
            saveSubscriptionConfig({
              establishment: establishmentId,
              data: payload,
              method: "post",
            }) as any,
          ).unwrap();
        }

        const res = await dispatch(
          patchSubscriptionBilling({
            establishment: establishmentId,
            data: payload,
          }) as any,
        ).unwrap();
        return res;
      } catch (e) {
        console.error("Erro ao atualizar cobrança:", e);
        return Promise.reject(e);
      }
    },
    [dispatch, establishmentId, hasPermission],
  );

  const updatePixApproval = useCallback(
    async (action: "approve" | "reject") => {
      if (!establishmentId) return;

      return dispatch(
        patchSubscriptionApproval({
          establishment: establishmentId,
          action,
        }),
      ).unwrap();
    },
    [dispatch, establishmentId],
  );

  return {
    // Suporta payloads no formato { data: {...} } e também payloads diretos
    config: data?.data ?? data ?? null,
    loading,
    error,
    upsertFull,
    patchBilling,
    updatePixApproval,
  };
};

export default useSubscriptionConfig;
